import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Breadcrumbs, PageHeader, Table } from '~/components/data';
import { EntityAuditPanel } from '~/components/domain';
import { ProjectRefChip } from '~/components/domain/project/ProjectRefChip';
import { useToast } from '~/components/feedback';
import { Button, Dialog, Input, Select } from '~/components/primitives';
import { CancelPreviewViewer } from '~/components/stock';
import { fetchUsers } from '~/lib/api/admin';
import {
  cancelLandedCost,
  cancelPreviewLandedCost,
  fetchLandedCostById,
  submitLandedCost,
} from '~/lib/api/landed-cost';
import { validateAuditHighlight } from '~/lib/audit/highlight-search';
import { formatDecimal, formatMoney, formatQty } from '~/lib/format';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/landed-cost/$id/')({
  // F105 (С-I): `?highlight=<audit_event_id>` — запись журнала, которую открыть в панели истории.
  validateSearch: validateAuditHighlight,
  staticData: { minMainWidth: 960 },
  component: LandedCostDetail,
});

function LandedCostDetail(): JSX.Element {
  const { id } = useParams({ from: '/_auth/landed-cost/$id/' });
  const { highlight: auditHighlight } = Route.useSearch();
  const qc = useQueryClient();
  const toast = useToast();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [submitApprover, setSubmitApprover] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelApprover, setCancelApprover] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const { data } = useQuery({
    queryKey: ['landed-cost', id],
    queryFn: () => fetchLandedCostById(id),
  });

  const cancelPreview = useQuery({
    queryKey: ['landed-cost', id, 'cancel-preview'],
    queryFn: () => cancelPreviewLandedCost(id),
    enabled: cancelOpen,
  });

  const approversQuery = useQuery({
    queryKey: ['admin', 'users', 'approvers-landed-cost'],
    queryFn: () => fetchUsers({ is_active: true }),
    enabled: submitOpen || cancelOpen,
  });

  const submitMut = useMutation({
    mutationFn: () => submitLandedCost(id, submitApprover ? { approver_id: submitApprover } : {}),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_lc_detail_toast_submitted() });
      void qc.invalidateQueries({ queryKey: ['landed-cost', id] });
      setSubmitOpen(false);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_lc_detail_toast_submit_failed(),
        description: err.message,
      }),
  });

  const cancelMut = useMutation({
    mutationFn: () =>
      cancelLandedCost(
        id,
        cancelReason.trim(),
        cancelApprover ? { approver_id: cancelApprover } : {},
      ),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_lc_detail_toast_cancelled() });
      void qc.invalidateQueries({ queryKey: ['landed-cost', id] });
      setCancelOpen(false);
      setCancelReason('');
      setCancelApprover('');
      setAcknowledged(false);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_lc_detail_toast_cancel_failed(),
        description: err.message,
      }),
  });

  const isDraft = data?.status === 'draft';
  const isApplied = data?.status === 'applied' || data?.status === 'submitted';
  const reasonValid = cancelReason.trim().length >= 10;
  const hasCriticalCancel = (cancelPreview.data?.warnings ?? []).some(
    (w) => w.severity === 'danger',
  );
  const eligibleApprovers = (approversQuery.data?.items ?? []).filter(
    (u) => u.is_active && (u.role === 'reviewer' || u.role === 'admin'),
  );

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_landed_cost(), to: '/landed-cost' },
              { label: data ? data.code : '…' },
            ]}
          />
        }
        title={
          data
            ? m.route_lc_detail_title_with_code({ code: data.code })
            : m.route_lc_detail_title_fallback()
        }
        description={
          data ? (
            <>
              {m.route_lc_detail_description({
                // FE-60 honest-audit: localize raw enum.
                status:
                  data.status === 'draft'
                    ? 'Черновик'
                    : data.status === 'applied' || data.status === 'submitted'
                      ? 'Проведён'
                      : data.status === 'cancelled'
                        ? 'Отменён'
                        : data.status,
                overhead: formatMoney({
                  amount: String(data.total_overhead),
                  currency: data.currency,
                }),
                currency: '',
              })}
              {(data as { project_id?: string | null }).project_id ? (
                <>
                  {' '}
                  <ProjectRefChip
                    projectId={(data as { project_id?: string | null }).project_id ?? null}
                  />
                </>
              ) : null}
            </>
          ) : undefined
        }
        actions={
          data && (
            <div className={shared.actions}>
              {isDraft && (
                <Link to="/landed-cost/$id/edit" params={{ id }}>
                  <Button variant="secondary">{m.common_button_edit()}</Button>
                </Link>
              )}
              <Button
                variant="primary"
                leadingIcon={<CheckCircle2 size={14} />}
                disabled={!isDraft}
                onClick={() => setSubmitOpen(true)}
                // FE-67 honest-audit: Tier 2 semantic via tooltip + visible chip рядом.
                title="Tier 2 — требуется подтверждение уровня 2 (audit)"
              >
                {m.route_lc_detail_btn_submit()}
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 6,
                    padding: '0 5px',
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    background: 'rgba(0,0,0,0.15)',
                    borderRadius: 3,
                  }}
                >
                  T2
                </span>
              </Button>
              <Button
                variant="destructive"
                leadingIcon={<XCircle size={14} />}
                disabled={!isApplied}
                onClick={() => setCancelOpen(true)}
              >
                {m.route_lc_detail_btn_cancel()}
              </Button>
            </div>
          )
        }
      />

      {data && (
        <>
          <section>
            <h3 className={shared.sectionTitle}>{m.route_lc_detail_charges_heading()}</h3>
            <Table
              ariaLabel={m.route_lc_detail_charges_aria()}
              data={data.charges ?? []}
              rowKey={(_, i) => `c-${i}`}
              columns={[
                { id: 'title', header: m.route_lc_detail_col_title(), accessor: (c) => c.title },
                {
                  // FE-N Pass 75: formatMoney({amount, currency}) вместо raw c.amount.
                  id: 'amount',
                  header: m.route_lc_detail_col_amount(),
                  accessor: (c) => formatMoney({ amount: c.amount, currency: c.currency }),
                  mono: true,
                  align: 'right',
                },
                {
                  id: 'cur',
                  header: m.route_lc_detail_col_currency(),
                  accessor: (c) => c.currency,
                  mono: true,
                },
                {
                  id: 'basis',
                  header: m.route_lc_detail_col_basis(),
                  accessor: (c) => c.basis,
                  mono: true,
                },
              ]}
            />
          </section>

          <section>
            <h3 className={shared.sectionTitle}>{m.route_lc_detail_sources_heading()}</h3>
            <Table
              ariaLabel={m.route_lc_detail_sources_aria()}
              data={data.source_items ?? []}
              rowKey={(_, i) => `s-${i}`}
              emptyTitle={m.route_lc_detail_sources_empty()}
              emptyDescription="Выберите Stock Entry receipts для allocation."
              emptyAction={
                isDraft ? (
                  <Link to="/landed-cost/$id/edit" params={{ id }}>
                    <Button variant="primary" size="sm">
                      {m.ui_privyazat_postuplenie()}
                    </Button>
                  </Link>
                ) : undefined
              }
              columns={[
                {
                  id: 'entry',
                  header: m.route_lc_detail_col_entry(),
                  accessor: (s) => (
                    <Link
                      to="/stock-entries/$id"
                      params={{ id: s.stock_entry_id }}
                      className={shared.link}
                    >
                      <span className={shared.mono}>
                        {s.stock_entry_label ?? s.stock_entry_id.slice(0, 8)}
                      </span>
                    </Link>
                  ),
                },
                {
                  id: 'item',
                  header: m.route_lc_detail_col_item(),
                  accessor: (s) => s.item_label ?? s.item_id.slice(0, 8),
                  mono: true,
                },
                {
                  // FE-N Pass 75: formatQty + uom.
                  id: 'qty',
                  header: m.route_lc_detail_col_qty(),
                  accessor: (s) => formatQty(s.qty, s.uom),
                  mono: true,
                  align: 'right',
                },
                {
                  // FE-N Pass 75: formatMoney({amount, currency: data.currency}).
                  id: 'amt',
                  header: m.route_lc_detail_col_amount(),
                  accessor: (s) => formatMoney({ amount: s.amount, currency: data.currency }),
                  mono: true,
                  align: 'right',
                },
                {
                  // FE-N Pass 75: formatDecimal (weight is plain decimal, no currency).
                  id: 'wt',
                  header: m.route_lc_detail_col_weight(),
                  accessor: (s) => (s.weight ? formatDecimal(s.weight) : '—'),
                  mono: true,
                  align: 'right',
                },
              ]}
            />
          </section>

          <section>
            <h3 className={shared.sectionTitle}>{m.route_lc_detail_allocations_heading()}</h3>
            <Table
              ariaLabel={m.route_lc_detail_allocations_aria()}
              data={data.allocations ?? []}
              rowKey={(_, i) => `a-${i}`}
              emptyTitle={m.route_lc_detail_allocations_empty()}
              columns={[
                {
                  id: 'item',
                  header: m.route_lc_detail_col_item(),
                  accessor: (a) => a.item_label ?? a.item_id.slice(0, 8),
                  mono: true,
                },
                {
                  // FE-N Pass 75: formatQty (qty plain decimal).
                  id: 'qty',
                  header: m.route_lc_detail_col_qty(),
                  accessor: (a) => formatQty(a.qty),
                  mono: true,
                  align: 'right',
                },
                {
                  // FE-N Pass 75: formatMoney для rates.
                  id: 'prev',
                  header: m.route_lc_detail_col_prev_rate(),
                  accessor: (a) =>
                    formatMoney({ amount: a.prev_valuation_rate, currency: data.currency }),
                  mono: true,
                  align: 'right',
                },
                {
                  id: 'delta',
                  header: m.route_lc_detail_col_delta(),
                  accessor: (a) =>
                    formatMoney({ amount: a.delta_per_unit, currency: data.currency }),
                  mono: true,
                  align: 'right',
                },
                {
                  id: 'new',
                  header: m.route_lc_detail_col_new_rate(),
                  accessor: (a) =>
                    formatMoney({ amount: a.new_valuation_rate, currency: data.currency }),
                  mono: true,
                  align: 'right',
                },
                {
                  id: 'tot',
                  header: m.route_lc_detail_col_total(),
                  accessor: (a) =>
                    formatMoney({ amount: a.total_allocated, currency: data.currency }),
                  mono: true,
                  align: 'right',
                },
              ]}
            />
          </section>
        </>
      )}

      <EntityAuditPanel entity_type="landed_cost" entity_id={id} highlight={auditHighlight} />

      <Dialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        title={m.route_lc_detail_submit_title()}
        size="md"
        footer={
          <div className={shared.rowEnd}>
            <Button variant="ghost" onClick={() => setSubmitOpen(false)}>
              {m.route_lc_detail_submit_cancel()}
            </Button>
            <Button
              variant="primary"
              disabled={submitMut.isPending}
              loading={submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              {m.route_lc_detail_submit_confirm()}
            </Button>
          </div>
        }
      >
        <p className={shared.subtle}>{m.route_lc_detail_submit_hint()}</p>
        <Select
          label={m.route_lc_detail_approver_label_opt()}
          value={submitApprover}
          onChange={(e) => setSubmitApprover(e.target.value)}
          options={[
            { value: '', label: m.route_lc_detail_approver_self() },
            ...eligibleApprovers.map((u) => ({
              value: u.id,
              label: m.route_lc_detail_approver_role_user({
                name: u.full_name ?? u.email,
                role: u.role,
              }),
            })),
          ]}
        />
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={m.route_lc_detail_cancel_title()}
        size="lg"
        footer={
          <div className={shared.rowEnd}>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              {m.route_lc_detail_cancel_back()}
            </Button>
            <Button
              variant="destructive"
              disabled={!reasonValid || cancelMut.isPending || (hasCriticalCancel && !acknowledged)}
              loading={cancelMut.isPending}
              onClick={() => cancelMut.mutate()}
            >
              {m.route_lc_detail_cancel_confirm()}
            </Button>
          </div>
        }
      >
        <CancelPreviewViewer
          data={cancelPreview.data}
          loading={cancelPreview.isLoading}
          acknowledged={acknowledged}
          onAcknowledgeChange={setAcknowledged}
        />
        <div className={shared.formGrid2}>
          <Select
            label={m.route_lc_detail_approver_label()}
            value={cancelApprover}
            onChange={(e) => setCancelApprover(e.target.value)}
            options={[
              { value: '', label: m.route_lc_detail_approver_choose() },
              ...eligibleApprovers.map((u) => ({
                value: u.id,
                label: m.route_lc_detail_approver_role_user({
                  name: u.full_name ?? u.email,
                  role: u.role,
                }),
              })),
            ]}
          />
          <Input
            label={m.route_lc_detail_reason_label()}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            error={
              cancelReason.length > 0 && !reasonValid
                ? m.route_lc_detail_reason_err_min()
                : undefined
            }
          />
        </div>
      </Dialog>
    </div>
  );
}
